'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { SERVICES_ITEMS } from '@/constants/ourservices';
import Link from 'next/link';

export default function ServicesSection({
  category_index,
}: {
  category_index: number;
}) {
  const [activeService, setActiveService] = useState(0);

  // Resetar activeService sempre que o category_index mudar
  useEffect(() => {
    setActiveService(0);
  }, [category_index]);

  const services = SERVICES_ITEMS.categories[category_index]?.services ?? [];

  // Verificação defensiva: só renderizar se o activeService for válido
  if (activeService >= services.length) {
    return null; // ou um loading/spinner, ou fallback amigável
  }

  const currentService = services[activeService];

  return (
    <div className="hidden lg:grid grid-cols-12 gap-6">
      {/* Left: Vertical Navigation */}
      <div className="col-span-3 flex flex-col gap-2">
        {services.map((service, idx) => (
          <Button
            key={idx}
            onClick={() => setActiveService(idx)}
            variant={'ghost'}
            className={`px-4 py-8 text-lg font-semibold border-b rounded-none border-stone-400 transition-colors justify-start ${
              activeService === idx ? 'text-orangecaixa' : 'text-bluecaixa'
            }`}
          >
            <Image
              width={24}
              height={24}
              alt="Seta pra direita"
              src={`/icons/arrow-right-circle-${
                activeService === idx ? 'orange' : 'blue'
              }.png`}
            />
            {service.title}
          </Button>
        ))}
      </div>

      {/* Middle: Description & Cards */}
      <div className="col-span-6 border border-stone-400 rounded-xl p-8 h-fit">
        <div className="text-base text-[#525252]">
          {currentService.description}
        </div>

        <div className="grid grid-cols-2 gap-4 my-6">
          {currentService.cards.map((card, idx) => (
            <div
              key={idx}
              className="flex flex-col border border-gray-300 rounded-md p-4 gap-2"
            >
              <Image
                src={card.icon_src}
                alt={card.title + ' icon'}
                height={44}
                width={44}
              />
              <h3 className="font-semibold text-lg text-bluecaixa">
                {card.title}
              </h3>
              <div className="text-base text-gray-600">{card.description}</div>
            </div>
          ))}
        </div>

        <Link
          href={currentService.href}
          className="btn w-full py-5 bg-orangecaixa text-white rounded-md text-xs lg:text-sm lg:font-semibold hover:text-stone-200 flex items-center justify-center gap-2"
        >
          <Image
            height={24}
            width={24}
            src="/icons/arrow-right-circle.png"
            alt="right arrow icon"
          />
          {currentService.btnText}
        </Link>
      </div>

      {/* Right: Image */}
      <div className="col-span-3 relative h-fit">
        <Image
          src={currentService.img_src}
          alt="Service Illustration"
          width={368}
          height={616}
          className="rounded-md object-cover w-full h-auto"
        />
      </div>
    </div>
  );
}
